import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink } from 'firebase/auth';
import { useAuth } from '../../components/AuthProvider';
import {
  Alert,
  Box,
  Button,
  Typography,
  CircularProgress,
  Container,
} from '@mui/material';
import LoginForm from '../../components/LoginForm';
import { auth } from '../../firebase_config';

const Login: React.FC = () => {
  const {
    signIn,
    user,
    loading,
    emailLinkVerified,
    pendingEmail,
    completeEmailLinkSignIn,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const linkAttempted = useRef(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(pendingEmail);
  const [isCompletingLink, setIsCompletingLink] = useState(false);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    if (user && emailLinkVerified) {
      navigate('/', { replace: true });
    }
  }, [user, emailLinkVerified, navigate]);

  useEffect(() => {
    if (
      loading ||
      linkAttempted.current ||
      !isSignInWithEmailLink(auth, window.location.href)
    ) {
      return;
    }

    linkAttempted.current = true;
    setIsCompletingLink(true);
    setLinkError('');

    completeEmailLinkSignIn(window.location.href)
      .then(() => navigate('/', { replace: true }))
      .catch((error: unknown) => {
        setLinkError(
          error instanceof Error
            ? error.message
            : '이메일 링크 인증에 실패했습니다. 다시 로그인해주세요.'
        );
      })
      .finally(() => setIsCompletingLink(false));
  }, [loading, completeEmailLinkSignIn, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#fff' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (email: string, password: string) => {
    const accountEmail = await signIn(email, password);
    setEmailSentTo(accountEmail);
  };

  const handleRestart = async () => {
    await logout();
    window.history.replaceState(null, '', '/login');
    setEmailSentTo(null);
    setLinkError('');
    linkAttempted.current = false;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        bgcolor: '#f8f8f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Container component="main" maxWidth="xs" sx={{ zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 480,
            bgcolor: '#fff',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(60,60,60,0.10)',
            px: 4,
            py: 5,
          }}
        >
          {/* WWF 로고 */}
          <Box
            component="img"
            src="/wwf-logo.png"
            alt="WWF Logo"
            sx={{
              width: 100,
              height: 100,
              mb: 2
            }}
          />

          <Typography 
            component="h1" 
            variant="h5" 
            sx={{ 
              mb: 1.5,
              color: '#1b1b1b',
              fontWeight: 700,
              textAlign: 'center',
              letterSpacing: '-0.5px'
            }}
          >
            <span lang="en">WWF</span> 클릭투기부
          </Typography>

          <Typography sx={{ color: '#888', fontSize: '1rem', mb: 3, textAlign: 'center' }}>
            WWF 관리자 전용 로그인 페이지입니다.
          </Typography>

          {isCompletingLink ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography>이메일 인증을 확인하고 있습니다.</Typography>
            </Box>
          ) : linkError ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="error" sx={{ mb: 2 }}>{linkError}</Alert>
              <Button fullWidth variant="contained" onClick={handleRestart}>
                로그인 다시 시도
              </Button>
            </Box>
          ) : emailSentTo ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {emailSentTo} 주소로 인증 링크를 보냈습니다.
              </Alert>
              <Typography sx={{ color: '#666', fontSize: '0.95rem', mb: 2, textAlign: 'center' }}>
                동일한 브라우저에서 링크를 열면 로그인이 완료됩니다.
              </Typography>
              <Button fullWidth variant="outlined" onClick={handleRestart}>
                다른 계정으로 로그인
              </Button>
            </Box>
          ) : (
            <LoginForm onSubmit={handleSubmit} />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
